// Lightweight wrapper around the browser Notification API.
// Gracefully degrades when permission is denied or the API is unavailable
// (older browsers, in-app webviews, iOS Safari without PWA install).

import { readLocal, writeLocal } from './storage';
import { log } from './logger';

const STORAGE_PROMPTED = 'fb_notif_prompted';

export const isSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

export const getPermission = () => {
  if (!isSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
};

export const wasPrompted = () => readLocal(STORAGE_PROMPTED, null) === '1';

const markPrompted = () => { writeLocal(STORAGE_PROMPTED, '1'); };

/** Request permission; returns the resulting state. Idempotent. */
export const requestPermission = async () => {
  if (!isSupported()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    const result = await Notification.requestPermission();
    markPrompted();
    return result;
  } catch (err) {
    log.warn('Notification permission prompt failed', err);
    return 'denied';
  }
};

const buildNotificationOptions = (body, opts) => ({
  body,
  icon: '/favicon.ico',
  badge: '/favicon.ico',
  tag: opts.tag,
  renotify: opts.renotify ?? false,
  requireInteraction: opts.requireInteraction ?? false,
  silent: opts.silent ?? false,
  data: opts.data,
});

const attachClickHandler = (n, opts) => {
  if (opts.onClick) {
    n.onclick = (e) => { e.preventDefault(); window.focus(); opts.onClick(n); n.close(); };
  } else {
    n.onclick = () => { window.focus(); n.close(); };
  }
};

/**
 * Fire a system notification (when permission granted).
 * Returns true if it actually fired, false otherwise — useful for fallback
 * to toast/in-page banners.
 */
export const notify = (title, body, opts = {}) => {
  if (!isSupported() || Notification.permission !== 'granted') return false;
  try {
    const n = new Notification(title, buildNotificationOptions(body, opts));
    attachClickHandler(n, opts);
    return true;
  } catch (err) {
    log.warn('Notification dispatch failed', err);
    return false;
  }
};
